import { MAX_LENGTH_BY_CODE_AND_FONT } from "./max_length_by_code_and_font";
import { ENGRAVING_TEXT_RULE_SET } from "./rules";

import {
  UnverifiedValue,
  VariationClassificationCode,
  FontName,
  MaxLengthByScript,
  ValidationInput,
  ValidationResult,
  ValidationContext,
  RuleStep,
} from "./types";

import { hasJapaneseScript } from "./validators";

/**
 * 選択書体の和文/英文それぞれの上限文字数を取得する
 * @param code バリエーション分類コード
 * @param font 選択書体
 * @returns 書体が持つ文字種別ごとの上限
 */
export function resolveMaxLengthByScript(code: VariationClassificationCode, font: FontName): MaxLengthByScript {
  const byCode = MAX_LENGTH_BY_CODE_AND_FONT[code];
  if (!(font in byCode)) {
    throw new Error("不正なバリエーション分類コードと書体の組み合わせです");
  }
  return byCode[font as keyof typeof byCode];
}

/**
 * 入力文字に応じて、今回適用する上限文字数を取得する
 * @param maxLengthByScript 書体の和文/英文上限
 * @param value 入力文字
 */
export function resolveEffectiveMaxLength(maxLengthByScript: MaxLengthByScript, value: UnverifiedValue): number {
  return hasJapaneseScript(value) ? maxLengthByScript.ja : maxLengthByScript.en;
}

/**
 * 商品（バリエーション分類コード）に日本語可能書体が1つでもあるか
 */
function resolveHasJapaneseCapableFont(code: VariationClassificationCode): boolean {
  return Object.values(MAX_LENGTH_BY_CODE_AND_FONT[code]).some((max) => max.ja > 0);
}

/**
 * バリデーション用に必要な情報の組み立て
 * @param input ユーザー入力内容、バリエーション分類コード、選択書体の情報
 * @returns バリデーション用に必要な情報のオブジェクト
 */
function buildContext(input: ValidationInput): ValidationContext {
  const maxLengthByScript = resolveMaxLengthByScript(input.code, input.font);
  const effectiveMaxLength = resolveEffectiveMaxLength(maxLengthByScript, input.value);

  return {
    value: input.value,
    code: input.code,
    font: input.font,
    effectiveMaxLength,
    allowJapanese: maxLengthByScript.ja > 0,
    hasJapaneseCapableFont: resolveHasJapaneseCapableFont(input.code),
  };
}

/**
 * ルールセットを順に評価し、最初に失敗したルールの結果を返す
 */
function runRules(ctx: ValidationContext, ruleSet: RuleStep[]): ValidationResult {
  for (const ruleStep of ruleSet) {
    if (ruleStep.fails(ctx)) {
      let message = ruleStep.message;
      if (typeof message === "function") {
        message = message(ctx);
      }

      return {
        ok: false,
        ruleId: ruleStep.id,
        message,
      };
    }
  }

  return { ok: true };
}

/**
 * 側面名入れのバリデーション
 * @param input ユーザーの入力内容、バリエーション分類コード、選択中の書体
 * @param ruleSet ルールセット（省略時は ENGRAVING_TEXT_RULE_SET。テストやルール差し替え用）
 * @return バリデーション結果
 */
export function validateSideName(
  input: ValidationInput,
  ruleSet: RuleStep[] = ENGRAVING_TEXT_RULE_SET
): ValidationResult {
  return runRules(buildContext(input), ruleSet);
}
